/**
 * Global sidenav state management composable
 * Manages open/closed state for all sidenav contexts in the application
 */
import { ref, computed } from 'vue'
import { createGlobalState } from '@vueuse/core'

export type SidenavType = 'cart' | 'menu' | 'search' | 'filter' | 'searchFilter'

interface SidenavState {
    cart: boolean
    menu: boolean
    search: boolean
    filter: boolean
    searchFilter: boolean
}

const useSidenavState = createGlobalState(() => {
    return {
        state: ref<SidenavState>({
            cart: false,
            menu: false,
            search: false,
            filter: false,
            searchFilter: false,
        }),
        cartTriggerContext: ref<string>(''),
    }
})

export const useSidenav = () => {
    const { state, cartTriggerContext } = useSidenavState()

    /**
     * Opens a specific sidenav and closes all others
     */
    const open = (type: SidenavType, metadata?: { triggerContext?: string }) => {
        closeAll()
        state.value[type] = true
        if (type === 'cart') {
            cartTriggerContext.value = metadata?.triggerContext ?? ''
        }
    }

    /**
     * Closes a specific sidenav
     */
    const close = (type: SidenavType) => {
        state.value[type] = false
        if (type === 'cart') {
            cartTriggerContext.value = ''
        }
    }

    /**
     * Toggles a specific sidenav state
     */
    const toggle = (type: SidenavType, metadata?: { triggerContext?: string }) => {
        if (state.value[type]) {
            close(type)
        } else {
            open(type, metadata)
        }
    }

    /**
     * Closes all sidenavs
     */
    const closeAll = () => {
        Object.keys(state.value).forEach(key => {
            state.value[key as SidenavType] = false
        })
        cartTriggerContext.value = ''
    }

    /**
     * Returns a computed ref for v-model binding with a specific sidenav
     */
    const isOpen = (type: SidenavType) => {
        return computed({
            get: () => state.value[type],
            set: (value: boolean) => {
                if (value) {
                    open(type)
                } else {
                    close(type)
                }
            }
        })
    }

    /**
     * Whether any sidenav is open
     */
    const anyOpen = computed(() => Object.values(state.value).some(Boolean))

    return {
        open,
        close,
        toggle,
        closeAll,
        isOpen,
        anyOpen,
        cartTriggerContext,
    }
}
